<template>
  <div class="flex flex-col p-4 w-full h-full">
    <SimpleTable
      :headers="tableHeaders"
      :data="rows"
      :pagination="simpleTablePagination"
      :height="'calc(100vh - (180px + var(--banner-height)))'"
      :show-actions-column="false"
      :show-page-size="!hasBackendPagination"
      :force-x-virtual="true"
      :auto-height="false"
      :fit-to-container-width="true"
      :gap="2"
      :table-props="{ columnWidth: 180, rowHeight: 46 }"
      identifier-key="_row_id"
      @update:pagination="handlePaginationUpdate"
    >
      <template #bodyCell="{ column, record }">
        <div
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          :title="formatVisualizationDateTimeValue(record[column.dataIndex])"
        >
          {{ formatVisualizationDateTimeValue(record[column.dataIndex]) }}
        </div>
      </template>
    </SimpleTable>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { formatVisualizationDateTimeValue } from "@/src/utils/dateTimeUtils.js";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import { paginationDefaults } from "@/src/constants/analyticConstants.js";

const props = defineProps({
  source: { type: String, default: "table_chart" },
  chartData: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["pageChange"]);

const pagination = ref({
  current: 1,
  pageSize: paginationDefaults.pageSize,
});

const columns = computed(() => props.chartData?.columns || []);

const allRows = computed(() =>
  (props.chartData?.rows || []).map((row, index) => ({
    ...row,
    id: row.id ?? `analytics_row_${index}`,
    _row_id: `analytics_row_${index}`,
  })),
);

const hasBackendPagination = computed(() => !!props.chartData?.pagination);

const rows = computed(() => {
  if (hasBackendPagination.value) return allRows.value;
  const { current, pageSize } = pagination.value;
  const start = (current - 1) * pageSize;
  return allRows.value.slice(start, start + pageSize);
});

const tableHeaders = computed(() =>
  columns.value.map((col) => ({
    ...col,
    label: col.title || col.label || col.key,
    width: 180,
    ellipsis: true,
  })),
);

const simpleTablePagination = computed(() => {
  const p = props.chartData?.pagination;
  if (p) {
    return {
      current: pagination.value.current,
      pageSize: p.page_size || pagination.value.pageSize,
      total: p.total || 0,
      showCount: true,
      showFirstLastPage: true,
      maxDataLimit: p.total || 0,
    };
  }
  const total = allRows.value.length;
  if (total === 0) return null;
  return {
    current: pagination.value.current,
    pageSize: pagination.value.pageSize,
    total,
    showCount: true,
    showFirstLastPage: true,
    maxDataLimit: total,
  };
});

watch(
  () => props.chartData,
  (newData, oldData) => {
    const p = newData?.pagination;
    if (p?.page) {
      pagination.value = { ...pagination.value, current: p.page };
    } else {
      const newTotal = p?.total;
      const oldTotal = oldData?.pagination?.total;
      if (!p || newTotal !== oldTotal) {
        pagination.value = { ...pagination.value, current: 1 };
      }
    }
  },
);

const handlePaginationUpdate = (updatedPagination) => {
  if (hasBackendPagination.value) {
    pagination.value = { ...pagination.value, current: updatedPagination.current };
    emit("pageChange", { page: updatedPagination.current });
  } else {
    pagination.value = {
      current: updatedPagination.current,
      pageSize: updatedPagination.pageSize || pagination.value.pageSize,
    };
  }
};
</script>

<style scoped>
:deep(.surely-table-header-cell) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
:deep(.surely-table-body-cell) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
</style>



